import { useEffect } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { Toaster } from "sonner";

import { ErrorFallback } from "~/components/ErrorFallback";
import { AuthProvider } from "~/features/auth/AuthProvider";
import { initAnalytics } from "~/lib/analytics";
import "./app.css";

export function Layout({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Module-level guard inside initAnalytics prevents HMR re-init.
    // Runs once per page load. No-op if VITE_POSTHOG_KEY is missing.
    initAnalytics();
  }, []);

  return (
    <html lang="en" data-theme="dark">
      <head>
        {/* Pre-paint theme hydration — sets data-theme from
            localStorage / prefers-color-scheme before any CSS applies, eliminating
            FOUC. The fallback `data-theme="dark"` on <html> stays for the
            JS-disabled case. Phase 4a. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var c=localStorage.getItem("csnx:theme:v1");if(c!=="light"&&c!=="dark"&&c!=="system")c="system";var e=c==="system"?(window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light"):c;document.documentElement.setAttribute("data-theme",e);}catch(_){document.documentElement.setAttribute("data-theme","dark");}})();`,
          }}
        />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Default title — page-level components can override via React 19's
            head-hoisting (rendering <title> in their JSX). Phase 4b: required
            for axe/Lighthouse `document-title` rule. */}
        <title>CSnx Information Center</title>
        <Meta />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <Links />
      </head>
      <body>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-card focus:text-foreground focus:border focus:border-border focus:rounded focus:shadow-lg"
        >
          Skip to content
        </a>
        {children}
        <ScrollRestoration />
        <Scripts />
        <Toaster position="top-right" richColors />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

export function ErrorBoundary() {
  return <ErrorFallback />;
}
